import { TLang } from "@/i18n";
import type { StreamBlock } from '@/utils/responsesStream';

/**
 * 递归深度可选类型 - 将对象及其所有嵌套属性都设为可选
 */
export type DeepPartial<T> = T extends object
  ? {
      [P in keyof T]?: DeepPartial<T[P]>;
    }
  : T;

export type MessageRole = 'user' | 'assistant';

/**
 * - sending   : 发送中（本期暂未使用）
 * - running   : 请求已发出，等待响应中（非流式阶段用于 loading 控制）
 * - success   : 请求成功，内容已渲染
 * - error     : 请求失败
 * - cancelled : 用户已取消请求
 * - streaming : @stream-reserved 流式输出中，本期非流式请求暂不使用，后续接入 SSE 时启用
 */
export type MessageStatus = 'sending' | 'running' | 'success' | 'error' | 'cancelled' | 'streaming';

export interface ChartField {
  /** 字段标识，对应 data 中的 key */
  code: string;
  /** 展示名称 */
  name?: string;
}

export interface ChartData {
  type: 'bar' | 'line' | 'pie' | 'table';
  title?: string;
  /** 维度字段列表（X 轴 / 类目轴） */
  dimensions?: ChartField[];
  /** 指标字段列表（Y 轴 / 数值轴） */
  metrics?: ChartField[];
  series?: object;
  data?: unknown[];
}

export interface MessageContent {
  text?: string;
  /**
   * source 为 Mind 时，按 SSE 到达顺序排列的正文、过程条和追问。
   * 有该字段就走时间线，不再把进度和正文拆开。
   */
  blocks?: StreamBlock[];
  /** 业务分析轮询阶段的进度 markdown，与正式正文分离渲染 */
  progressText?: string;
  /** 进度文案显示状态：running 显示，hidden 隐藏 */
  progressStatus?: 'running' | 'cancelled' | 'succeeded' | 'failed';
  type?: 'text' | 'data_chart';
  /** type=data_chart 时的图表 schema */
  chart?: ChartData;
  /** 原始数据行（用于表格视图） */
  tableData?: unknown[];
  sql?: string;
  /**
   * 分段模式：sqlDataChat 返回的 record_id，点击「生成图表」时传给 recordChat。
   * 与 pendingSqlChatId 一起绑定在消息上，确保跨模式切换后仍能找回正确的上下文。
   */
  pendingSqlRecordId?: string;
  /** 分段模式：发出 sqlDataChat 时使用的 chatId，保证 recordChat 用同一会话 ID */
  pendingSqlChatId?: string;
  /**
   * true 时只渲染图表视图，不显示图表/表格切换按钮。
   * recordChat 返回的结果无原始数据行，仅含 chart schema，使用此标志。
   */
  chartOnly?: boolean;
}

export type FeedbackType = 'good' | 'bad' | null;

export interface MessageBizPayload {
  role: ERole;
  accessToken?: string;
  chatId?: number | string;
  chatRecordId?: number | string;
  sessionId?: string;
  turnId?: string;
}

interface BaseChatMessage {
  id: string;
  role: MessageRole;
  /** 业务参数 */
  bizPayload: MessageBizPayload;
  /** 消息内容主体 */
  content: MessageContent;
  /** 消息状态 */
  status: MessageStatus;
  /** 消息创建时间戳（毫秒） */
  timestamp: number;
  /** sessionContinue 返回的 record_id，用于反馈接口 */
  recordId?: number;
  /** 用户反馈状态，持久化在消息上 */
  feedback?: FeedbackType;
  /** 分段模式：是否正在等待用户点击「生成图表」 */
  awaitingChart?: boolean;
  /**
   * 失败 / 取消时显示在消息下方的状态说明文字。
   * 有具体原因时直接展示，无原因时由 UI 使用兜底文案。
   */
  statusText?: string;
}

/** 默认/业务分析消息，不包含分段模式特有字段 */
export interface CommonChatMessage extends BaseChatMessage {
  type?: 'default' | 'business-analysis';
  awaitingChart?: never;
}

/** 分段模式助手消息，包含等待生成图表状态 */
export interface SegmentAssistantMessage extends BaseChatMessage {
  role: 'assistant';
  type: 'segment';
  awaitingChart?: boolean;
}

export type ChatMessage = CommonChatMessage | SegmentAssistantMessage;

export interface QuickQuestion {
  id: string;
  text: string;
}

export interface ChatRole {
  id: ERole;
  label: string;
  description?: string;
  solutionCode: string;
  agentRoleCode: string;
  schema?: string;
  isDisabled?: boolean;
}

export interface SolutionAgentRole {
  solutionCode: string;
  agentRoleCode: string;
  agentRoleName: string;
  schema?: string;
  isDefault?: boolean;
  isDisabled?: boolean;
}

export interface SendMessageParams {
  content: string;
  roleId: string;
  conversationId?: string;
}

export interface ChatSession {
  id: string;
  title: string;
  createdAt: number;
  updatedAt: number;
  messages: ChatMessage[];
}

export interface ConversationTurn {
  turn_key: string;
  question: string;
  answer: string;
  created_at: string;
}

export interface ConversationSessionItem {
  uid: string;
  channel: 'chat' | 'agent';
  tenant_code: string;
  solution_code: string;
  biz_type: string;
  session_key: string;
  updated_at: string;
  turns: ConversationTurn[];
}

export interface ConversationSessionsResponse {
  total: number;
  page: number;
  page_size: number;
  items: ConversationSessionItem[];
}

export interface DevCenterSessionInfo {
  type: 'dev-center';
  accessToken: string;
  chatId: number;
}

export interface BossReportSessionInfo {
  type: 'boss-report';
  sessionId: string;
}

/** 后端返回的会话信息（按方案区分） */
export type SessionInfo = DevCenterSessionInfo | BossReportSessionInfo;

// 角色枚举
export enum ERole {
  ANALYST = 'analyst',
  BUSINESS_ANALYSIS = 'businessAnalysis',
}

// 数据分析师角色
export enum EAnalyticsMode {
  FAST = 'fast',
  SEGMENT = 'segment',
}

// 业务场景类型枚举（可根据实际情况扩展）
export enum ESolutionCode {
  DEV_CENTER = 'S_DEV_CENTER', // 开发者中心
  BOSS_REPORT = 'BOSS_REPORT', // 经营分析
}

/** Macaw.init.source，用于区分 GA 上报端 */
export type GaHostSource = 'IPOS_WEB' | 'IPOS_REPORT_H5' | 'GPOS_WEB' | 'GPOS_REPORT_H5' | 'STARFISH_WEB' | 'STARFISH_REPORT_H5';

export interface ICommon {
  token?: string;
  tenantCode?: string;
  orgId?: string;
  storeId?: string;
  language?: TLang;
  bizType?: string;
  agentRoleCode?: string;
  solution_code?: ESolutionCode | string;
  source?: GaHostSource | string;
  /** 门店时区，如 Asia/Bangkok。宿主未下发时回退到浏览器时区 */
  timezone?: string;
  /** 门店币种，如 THB。本工程无来源，仅透传宿主下发值 */
  currency?: string;
  /** 商户身份角色，如 merchant_owner。本工程无来源，仅透传宿主下发值 */
  roles?: string;
  /**
   * init 传入的扩展会话字段。
   * 请求 extraSession 时与 org_id、store_id 合并，同名字段以这里为准。
   */
  extraSession?: Record<string, string | number | boolean | null>;
}

export interface UserInfo {
  email?: string;
  uid?: string;
  phone?: string;
}

export interface IProgressHistoryItem {
  message: string;
  kind?: string;
}

// 分析状态类型
export type TAnalysisStatus = "succeeded" | "running" | "queued" | "failed" | "cancelled";
